import { useEffect, useState } from "react";

import { toast } from "sonner";

import DocumentUploadInput from "../../../components/shared/DocumentUploadInput";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { useApi } from "../../../context/ApiContext";
import { cn, getCharCount } from "../../../lib/utils.js";
import ContractorDialog from "../../ContractorCosting/component/ContractorDialog";
import InvoiceDialog from "../../Invoices/component/InvoiceDialog";
import VendorDialog from "../../Vendors/component/VendorDialog";

const PaymentDialog = ({
  isOpen,
  onOpenChange,
  onSuccess,
  selectedProject = "",
  selectedVendor = "",
  selectedInvoice = "",
  editingPayment = null,
  vendors = []
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    payeeType: "vendor",
    vendorId: selectedVendor || "",
    contractorId: "",
    invoiceId: selectedInvoice || "",
    amount: "",
    paymentMethod: "bank_transfer",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentStatus: "completed",
    referenceNumber: "",
    notes: "",
    projectId: selectedProject || "",
    documents: []
  });

  const [isVendorDialogOpen, setIsVendorDialogOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isContractorDialogOpen, setIsContractorDialogOpen] = useState(false);

  const isNotesOverLimit = getCharCount(formData.notes) > 500;

  useEffect(() => {
    if (editingPayment) {
      setFormData({
        payeeType: editingPayment.payeeType || "vendor",
        vendorId: editingPayment.vendorId || "",
        contractorId: editingPayment.contractorId || "",
        invoiceId: editingPayment.invoiceId || "",
        amount: editingPayment.amount?.toString() || "",
        paymentMethod: editingPayment.paymentMethod || "bank_transfer",
        paymentDate: editingPayment.paymentDate
          ? new Date(editingPayment.paymentDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        paymentStatus: editingPayment.paymentStatus || "completed",
        referenceNumber: editingPayment.referenceNumber || "",
        notes: editingPayment.notes || "",
        projectId: editingPayment.project?.id || editingPayment.projectId || selectedProject || "",
        documents: editingPayment.documents || []
      });
    } else {
      setFormData({
        payeeType: "vendor",
        vendorId: selectedVendor || "",
        contractorId: "",
        invoiceId: selectedInvoice || "",
        amount: "",
        paymentMethod: "bank_transfer",
        paymentDate: new Date().toISOString().split("T")[0],
        paymentStatus: "completed",
        referenceNumber: "",
        notes: "",
        projectId: selectedProject || "",
        documents: []
      });
    }
  }, [editingPayment, selectedProject, selectedVendor, selectedInvoice, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNotesOverLimit) {
      toast.error("Notes exceed the 500-character limit.");
      return;
    }
    if (!formData.projectId) {
      toast.error("Please select a project");
      return;
    }
    if (formData.payeeType === "vendor" && !formData.vendorId) {
      toast.error("Please select a vendor");
      return;
    }
    if (formData.payeeType === "contractor" && !formData.contractorId) {
      toast.error("Please select a contractor");
      return;
    }
    setLoading(true);
    try {
      let response;
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        vendorId: formData.payeeType === "vendor" ? formData.vendorId || null : null,
        contractorId: formData.payeeType === "contractor" ? formData.contractorId || null : null,
        invoiceId: formData.payeeType === "vendor" ? formData.invoiceId || null : null
      };

      if (editingPayment) {
        response = await api.put(`/payments/${editingPayment.id}`, payload);
        toast.success("Payment updated successfully!");
      } else {
        response = await api.post(`/payments`, payload);
        toast.success("Payment created successfully!");
      }

      if (onSuccess) onSuccess(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Error saving payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{editingPayment ? "Edit Payment" : "Create New Payment"}</DialogTitle>
            <DialogDescription>
              {editingPayment ? "Update payment information" : "Record a new payment transaction"}
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="payeeType">
                  Payee Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.payeeType}
                  onValueChange={(val) =>
                    setFormData({
                      ...formData,
                      payeeType: val,
                      vendorId: "",
                      contractorId: "",
                      invoiceId: ""
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payee type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.payeeType === "vendor" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="vendorId">
                      Vendor <span className="text-red-500">*</span>
                    </Label>
                    {!editingPayment && !selectedVendor && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-primary"
                        onClick={() => setIsVendorDialogOpen(true)}
                      >
                        + Add New
                      </Button>
                    )}
                  </div>
                  <LazySearchSelect
                    value={formData.vendorId}
                    onChange={(val) => setFormData({ ...formData, vendorId: val, invoiceId: "" })}
                    placeholder="Select Vendor"
                    searchPlaceholder="Search vendors..."
                    apiUrl="/vendors"
                    labelKey="vendorName"
                    disabled={!!selectedVendor && !editingPayment}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Invoice</Label>
                    {!editingPayment && !selectedInvoice && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-primary"
                        onClick={() => setIsInvoiceDialogOpen(true)}
                      >
                        + Add New
                      </Button>
                    )}
                  </div>
                  <LazySearchSelect
                    value={formData.invoiceId}
                    onChange={(val) => setFormData({ ...formData, invoiceId: val })}
                    placeholder="Select Invoice"
                    searchPlaceholder="Search invoices..."
                    apiUrl={formData.vendorId ? `/invoices/vendor/${formData.vendorId}` : "/invoices"}
                    valueFetchUrl={formData.invoiceId ? `/invoices/${formData.invoiceId}` : null}
                    labelKey="invoiceNumber"
                    popoverWidth="w-[var(--radix-popover-trigger-width)] md:w-[400px]"
                    disabled={!!selectedInvoice && !editingPayment}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="contractorId">
                      Contractor <span className="text-red-500">*</span>
                    </Label>
                    {!editingPayment && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-primary"
                        onClick={() => setIsContractorDialogOpen(true)}
                      >
                        + Add New
                      </Button>
                    )}
                  </div>
                  <LazySearchSelect
                    value={formData.contractorId}
                    onChange={(val) => setFormData({ ...formData, contractorId: val })}
                    placeholder="Select Contractor"
                    searchPlaceholder="Search contractors..."
                    apiUrl="/contractors"
                    labelKey="contractorName"
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>
                  Associated Project <span className="text-red-500">*</span>
                </Label>
                <LazySearchSelect
                  value={formData.projectId || "all"}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      projectId: val === "all" ? "" : val
                    })
                  }
                  placeholder="Select Project"
                  apiUrl="/projects"
                  showAllOption={true}
                  allOptionLabel="No Project"
                  popoverWidth="w-[var(--radix-popover-trigger-width)] md:w-[400px]"
                />
              </div>
              <div>
                <Label htmlFor="amount">
                  Amount <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="paymentMethod">
                  Payment Method <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.paymentMethod} onValueChange={(val) => setFormData({ ...formData, paymentMethod: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="paymentDate">
                  Payment Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="paymentDate"
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="paymentStatus">
                  Status <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.paymentStatus} onValueChange={(val) => setFormData({ ...formData, paymentStatus: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="partially">Partially Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="referenceNumber">Reference Number</Label>
                <Input
                  id="referenceNumber"
                  value={formData.referenceNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      referenceNumber: e.target.value
                    })
                  }
                  placeholder="Enter reference number"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Enter notes"
                className={cn(isNotesOverLimit && "border-red-500")}
                rows={2}
              />
              <WordCount text={formData.notes} />
            </div>

            <DocumentUploadInput
              value={formData.documents || []}
              onChange={(val) => setFormData({ ...formData, documents: val })}
              label="Payment Receipts / Documents (Optional)"
              multiple={true}
              helperText="Upload payment confirmation, check copy or receipts"
            />
          </div>
          <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
            <Button
              type="submit"
              variant="primary"
              className="w-full py-6 rounded-lg shadow-lg"
              disabled={loading || isNotesOverLimit}
            >
              {loading ? "Saving..." : editingPayment ? "Update Payment" : "Create Payment"}
            </Button>
          </div>
        </form>
      </DialogContent>

      <VendorDialog
        isOpen={isVendorDialogOpen}
        onOpenChange={setIsVendorDialogOpen}
        onSuccess={(newVendor) => {
          setFormData((prev) => ({
            ...prev,
            vendorId: newVendor.id,
            invoiceId: ""
          }));
        }}
      />

      <InvoiceDialog
        isOpen={isInvoiceDialogOpen}
        onOpenChange={setIsInvoiceDialogOpen}
        selectedVendor={formData.vendorId}
        selectedProject={formData.projectId}
        onSuccess={(newInvoice) => {
          setFormData((prev) => ({
            ...prev,
            invoiceId: newInvoice.id,
            vendorId: newInvoice.vendorId || prev.vendorId,
            projectId: newInvoice.projectId || prev.projectId,
            amount: newInvoice.amount?.toString() || prev.amount
          }));
        }}
      />

      <ContractorDialog
        isOpen={isContractorDialogOpen}
        onOpenChange={setIsContractorDialogOpen}
        onSuccess={(newContractor) => {
          setFormData((prev) => ({ ...prev, contractorId: newContractor.id }));
        }}
      />
    </Dialog>
  );
};

export default PaymentDialog;
